import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  DEFAULT_DASHBOARD_STATE,
  LIVELLI,
  filtroFromFocusParam,
  focusParamFromFiltro,
  livelloFromModeParam,
  modeParamFromLivello,
} from "../types/dashboardMap";
import {
  PORTO_SLUG_TO_ID,
  writePortoJourney,
  readPortoJourney,
} from "../utils/portoJourney/portoJourneyState";

const InterMappaCtx = createContext(null);

function normalizePortoId(raw) {
  if (!raw) return null;
  if (raw.startsWith("porto-")) return raw;
  return PORTO_SLUG_TO_ID[raw] || `porto-${raw}`;
}

function parseUrlDashboard(searchParams) {
  const mode = searchParams.get("mode") || searchParams.get("canale");
  const livello =
    livelloFromModeParam(mode) ||
    (searchParams.get("mode") === "terra" || searchParams.get("canale") === "terra"
      ? LIVELLI.TERRA
      : searchParams.get("canale") === "mare"
        ? LIVELLI.MARE
        : null);

  const fromPorto = searchParams.get("fromPorto");
  const portoParam = searchParams.get("porto");
  const portoSelezionatoId = normalizePortoId(fromPorto || portoParam);

  const comuneRaw = searchParams.get("comune");
  const comuneSelezionatoId = comuneRaw
    ? comuneRaw.startsWith("comune-")
      ? comuneRaw
      : `comune-${comuneRaw}`
    : null;

  const filtroAttivo = filtroFromFocusParam(searchParams.get("focus")) || "TUTTO";
  const macroRaw = (searchParams.get("macro") || "").toLowerCase();
  const macro =
    macroRaw === "montagna" ? "montagna" : macroRaw === "terra" ? "terra" : null;

  return {
    mappaAttiva: livello,
    portoSelezionatoId,
    comuneSelezionatoId,
    filtroAttivo,
    macro,
  };
}

function buildSearchParams(state, current) {
  const params = new URLSearchParams(current);

  params.set("mode", modeParamFromLivello(state.mappaAttiva));
  params.delete("canale");

  if (state.mappaAttiva === LIVELLI.MARE) {
    params.delete("macro");
  } else if (state.macro === "montagna" || state.macro === "terra") {
    params.set("macro", state.macro);
  }

  if (state.portoSelezionatoId && state.mappaAttiva === LIVELLI.TERRA) {
    params.set("fromPorto", state.portoSelezionatoId);
  } else {
    params.delete("fromPorto");
  }

  if (state.comuneSelezionatoId) {
    params.set("comune", state.comuneSelezionatoId.replace(/^comune-/, ""));
  } else {
    params.delete("comune");
  }

  const focus = focusParamFromFiltro(state.filtroAttivo);
  if (focus) params.set("focus", focus);
  else params.delete("focus");

  return params;
}

/**
 * Stato condiviso Mare ↔ Terra (porto origine, livello mappa, filtri URL-sync).
 */
const MAP_PATH_PREFIXES = ["/mappa-globale", "/esplora-la-mappa", "/rotta-adriatica"];

function isMapRoute(pathname) {
  return MAP_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function InterMappaProvider({ children }) {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const onMapRoute = isMapRoute(pathname);
  const urlPatch = useMemo(
    () => (onMapRoute ? parseUrlDashboard(searchParams) : {}),
    [searchParams, onMapRoute],
  );

  const [portoOrigine, setPortoOrigine] = useState(null);
  const [dashboard, setDashboard] = useState(() => {
    const journey = readPortoJourney();
    const base = { ...DEFAULT_DASHBOARD_STATE };
    if (journey.portoSlug) {
      base.portoSelezionatoId = normalizePortoId(journey.portoSlug);
    }
    if (journey.fromPorto && journey.terraActivatedAt) {
      base.mappaAttiva = LIVELLI.TERRA;
    }
    return base;
  });

  useEffect(() => {
    if (!onMapRoute) return;
    setDashboard((prev) => ({
      ...prev,
      ...(urlPatch.mappaAttiva != null ? { mappaAttiva: urlPatch.mappaAttiva } : {}),
      ...(Object.prototype.hasOwnProperty.call(urlPatch, "portoSelezionatoId")
        ? { portoSelezionatoId: urlPatch.portoSelezionatoId }
        : {}),
      comuneSelezionatoId: urlPatch.comuneSelezionatoId ?? null,
      filtroAttivo: urlPatch.filtroAttivo || "TUTTO",
      macro: urlPatch.macro ?? prev.macro ?? "terra",
    }));
  }, [
    onMapRoute,
    urlPatch,
  ]);

  const syncUrl = useCallback(
    (next) => {
      if (!onMapRoute) return;
      setSearchParams((current) => buildSearchParams(next, current), { replace: true });
    },
    [setSearchParams, onMapRoute],
  );

  const patchDashboard = useCallback(
    (patch) => {
      setDashboard((prev) => {
        const next = { ...prev, ...patch };
        syncUrl(next);
        return next;
      });
    },
    [syncUrl],
  );

  const attivaMappaTerra = useCallback(
    (portoIdOrPorto = null) => {
      let portoId = null;
      let portoObj = null;
      if (portoIdOrPorto && typeof portoIdOrPorto === "object") {
        portoObj = portoIdOrPorto;
        portoId = portoIdOrPorto.id || null;
      } else if (portoIdOrPorto) {
        portoId = normalizePortoId(portoIdOrPorto);
      }
      if (portoObj) setPortoOrigine(portoObj);
      setDashboard((prev) => {
        const next = {
          ...prev,
          mappaAttiva: LIVELLI.TERRA,
          portoSelezionatoId: portoId || prev.portoSelezionatoId,
          macro: prev.macro || "terra",
        };
        syncUrl(next);
        return next;
      });
      if (portoId) {
        const slug = portoId.replace(/^porto-/, "");
        writePortoJourney({ fromPorto: true, portoSlug: slug, terraActivatedAt: new Date().toISOString() });
      }
    },
    [syncUrl],
  );

  const attivaMappaMare = useCallback(() => {
    patchDashboard({
      mappaAttiva: LIVELLI.MARE,
      filtroAttivo: "TUTTO",
      macro: null,
    });
  }, [patchDashboard]);

  const setFiltroAttivo = useCallback(
    (filtro) => patchDashboard({ filtroAttivo: filtro }),
    [patchDashboard],
  );

  const setComuneSelezionatoId = useCallback(
    (comuneId) => patchDashboard({ comuneSelezionatoId: comuneId }),
    [patchDashboard],
  );

  const resetInterMappa = useCallback(() => {
    setPortoOrigine(null);
    setDashboard(DEFAULT_DASHBOARD_STATE);
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const attivaMappaTerraDaPorto = useCallback(
    (porto) => attivaMappaTerra(porto),
    [attivaMappaTerra],
  );

  const mappaTerraAttiva = dashboard.mappaAttiva === LIVELLI.TERRA;

  const value = useMemo(
    () => ({
      ...dashboard,
      portoOrigine,
      setPortoOrigine,
      mappaTerraAttiva,
      setMappaTerraAttiva: (on) =>
        on ? attivaMappaTerra(dashboard.portoSelezionatoId) : attivaMappaMare(),
      attivaMappaTerra,
      attivaMappaMare,
      attivaMappaTerraDaPorto,
      setFiltroAttivo,
      setComuneSelezionatoId,
      patchDashboard,
      resetInterMappa,
    }),
    [
      dashboard,
      portoOrigine,
      mappaTerraAttiva,
      attivaMappaTerra,
      attivaMappaMare,
      attivaMappaTerraDaPorto,
      setFiltroAttivo,
      setComuneSelezionatoId,
      patchDashboard,
      resetInterMappa,
    ],
  );

  return <InterMappaCtx.Provider value={value}>{children}</InterMappaCtx.Provider>;
}

export function useInterMappa() {
  const ctx = useContext(InterMappaCtx);
  if (!ctx) {
    throw new Error("useInterMappa must be used within InterMappaProvider");
  }
  return ctx;
}

/** Safe hook quando il provider non è montato (pagine legacy). */
export function useInterMappaOptional() {
  return useContext(InterMappaCtx);
}
